import './lib/polyfills'
import { createRoot } from 'react-dom/client'
import { IconContext } from '@phosphor-icons/react'
import App from './App.tsx'
import { AppUpdateNotice } from './components/AppUpdateNotice'
import '@fontsource/poppins/400.css'
import '@fontsource/poppins/500.css'
import '@fontsource/poppins/600.css'
import '@fontsource/poppins/700.css'
// Inter = the public pages' body face (Poppins headings / Inter body — the
// fixed LIGHTMASTER_PUBLIC_THEME pairing). Self-hosted so customer devices
// never call the Google Fonts CDN. @font-face is lazy: admin sessions render
// no Inter, so these weights are only ever downloaded on public pages.
import '@fontsource/inter/400.css'
import '@fontsource/inter/500.css'
import '@fontsource/inter/600.css'
import '@fontsource/inter/700.css'
import './index.css'
import { initSentry } from './lib/sentry'
import { initAnalytics } from './lib/analytics'
import { requestChunkReload } from './lib/lazyWithReload'

// Initialize Sentry as early as possible
initSentry()
// Product analytics loads lazily and only when VITE_POSTHOG_KEY is set.
void initAnalytics()

// Self-heal stale deploys. A browser holding a cached index.html from a
// previous deploy lazy-imports hashed chunks that no longer exist; Vercel's
// SPA rewrite then serves index.html as the module (MIME text/html) and the
// import fails — e.g. the mockup editor's PixiJS renderer silently dying
// (2026-07-20 prod incident). Vite surfaces exactly this as vite:preloadError:
// reload once to pick up the fresh index.html. sessionStorage throttle so a
// genuinely broken deploy can't reload-loop the tab.
// Shares requestChunkReload with lazyWithReload so both know when a reload is
// already on its way. They used to keep separate copies of this throttle and
// could not see each other's attempt, so a preloadError reload here made the
// lazy loader over there throw instead of waiting (LIGHTMASTER-WEB-19).
window.addEventListener('vite:preloadError', (event) => {
  if (requestChunkReload()) event.preventDefault()
})

// Standardize Phosphor icon weight app-wide. The icon barrel
// (src/components/icons) passes an explicit weight per glyph (regular default,
// bold for carets/affordances, fill for status dots) which overrides this; this
// provider is the single global default so any icon imported directly from
// @phosphor-icons/react can't drift off the 'regular' standard.
createRoot(document.getElementById("root")!).render(
  <IconContext.Provider value={{ weight: 'regular' }}>
    <AppUpdateNotice />
    <App />
  </IconContext.Provider>
);
